    import React, { useEffect, useState, useRef, useCallback } from "react";
    import { Link, useNavigate } from "react-router-dom";
    import emailjs from "@emailjs/browser";
    import { useMotionValue, useSpring, useInView } from "framer-motion";
    import { collegesDetails } from "./data/collegesDetails.js";
    import TextType from "./TextType";
    import TestimonialsSection from './components/layout/TestimonialsSection';
    import Navbar from './components/layout/Navbar';
    import CommonApplicationForm from './components/CommonApplicationForm';
    import SEO from './components/SEO';
    import ApplyModal from './components/layout/ApplyModal';
    import GeuModal from './components/layout/GeuModal';
    

    // --- HELPER FUNCTIONS (Outside Main Component) ---

    function openModal() {
      const overlay = document.getElementById("applyOverlay");
      const form = document.getElementById("applicationForm");
      const success = document.getElementById("successMessage");
      if (overlay) overlay.style.display = "flex";
      if (form) form.style.display = "block";
      if (success) success.classList.remove("active");
    }

    function closeModal() { 
      const overlay = document.getElementById("applyOverlay");
      const form = document.getElementById("applicationForm");
      const success = document.getElementById("successMessage");
      if (overlay) overlay.style.display = "none";
      if (form) form.style.display = "block";
      if (success) success.classList.remove("active");
    }

    // CountUp Component
    function CountUp({ to, from = 0, direction = 'up', delay = 0, duration = 2, className = '', startWhen = true, separator = '', onStart, onEnd }) {
      const ref = useRef(null);
      const motionValue = useMotionValue(direction === 'down' ? to : from);
      const damping = 20 + 40 * (1 / duration);
      const stiffness = 100 * (1 / duration);
      const springValue = useSpring(motionValue, { damping, stiffness });
      const isInView = useInView(ref, { once: true, margin: '0px' });

      const getDecimalPlaces = num => {
        const str = num.toString();
        if (str.includes('.')) {
          const decimals = str.split('.')[1];
          if (parseInt(decimals) !== 0) return decimals.length;
        }
        return 0;
      };

      const maxDecimals = Math.max(getDecimalPlaces(from), getDecimalPlaces(to));

      const formatValue = useCallback(latest => {
        const options = { useGrouping: !!separator, minimumFractionDigits: maxDecimals > 0 ? maxDecimals : 0, maximumFractionDigits: maxDecimals > 0 ? maxDecimals : 0 };
        const formatted = Intl.NumberFormat('en-US', options).format(latest);
        return separator ? formatted.replace(/,/g, separator) : formatted;
      }, [maxDecimals, separator]);

      useEffect(() => {
        if (isInView && startWhen) {
          if (onStart) onStart();
          const timeoutId = setTimeout(() => { motionValue.set(direction === 'down' ? from : to); }, delay * 1000);
          const durationTimeoutId = setTimeout(() => { if (onEnd) onEnd(); }, delay * 1000 + duration * 1000);
          return () => { clearTimeout(timeoutId); clearTimeout(durationTimeoutId); };
        }
      }, [isInView, startWhen, motionValue, direction, from, to, delay, onStart, onEnd, duration]);

      useEffect(() => {
        const unsubscribe = springValue.on('change', latest => {
          if (ref.current) ref.current.textContent = formatValue(latest);
        });
        return () => unsubscribe();
      }, [springValue, formatValue]);

      return <span className={className} ref={ref} />;
    }

    // --- MAIN COMPONENT ---
    export default function HomePage() {
  const [geuModalOpen, setGeuModalOpen] = useState(false);
  const [selectedGeuCollege, setSelectedGeuCollege] = useState(null);

      const formRef = useRef(null);
      const resultsRef = useRef(null);
      const searchInputRef = useRef(null);
      const cardRefs = useRef([]); 
      
      const [showCommonForm, setShowCommonForm] = useState(false);
      const [searchQuery, setSearchQuery] = useState("");
      const [searchResults, setSearchResults] = useState([]);
      const [phone, setPhone] = useState("");
      const [phoneError, setPhoneError] = useState("");
      const [typedPlaceholder, setTypedPlaceholder] = useState("");
      const [placeholder, setPlaceholder] = useState("Search colleges or courses...");
      
    const [applyModalOpen, setApplyModalOpen] = useState(false);
    const [selectedCollege, setSelectedCollege] = useState({ name: "General", source: "homepage" });
      const navigate = useNavigate();

      // 1. Popup Timer
  // In HomePage.js - Replace the existing useEffect with:
useEffect(() => {
  // Check if popup was already shown in this session
  const popupShown = sessionStorage.getItem('commonFormShown');
  
  if (!popupShown) {
    // Show popup after 5 seconds only if not shown before
    const timer = setTimeout(() => {
      setShowCommonForm(true);
      // Mark as shown for this session
      sessionStorage.setItem('commonFormShown', 'true');
    }, 5000);

    return () => clearTimeout(timer);
  }
}, []);




      // 2. Slideshow Logic
      useEffect(() => {
        const initSlideshow = (containerClass) => {
          let currentSlide = 0;
          const container = document.querySelector(containerClass);
          if (!container) return null;
          const slides = container.querySelectorAll('.hero-slide');
          const totalSlides = slides.length;
          if (slides.length === 0) return null;
          function nextSlide() {
            slides[currentSlide].classList.remove('active');
            currentSlide = (currentSlide + 1) % totalSlides;
            slides[currentSlide].classList.add('active');
          }
          slides[0].classList.add('active');
          return setInterval(nextSlide, 5000);
        };
        const topTimer = initSlideshow('.hero-slideshow-top');
        const bottomTimer = initSlideshow('.hero-slideshow-bottom');
        const fullTimer = initSlideshow('.hero-slideshow-full');
        return () => {
          if (topTimer) clearInterval(topTimer);
          if (bottomTimer) clearInterval(bottomTimer);
          if (fullTimer) clearInterval(fullTimer);
        };
      }, []);

      // 3. Animated Title
      useEffect(() => {
        const headline = "Find Your Perfect College in Dehradun";
        const words = headline.split(" ");
        const parent = document.querySelector(".hero-animated-title");
        if (parent) {
          parent.innerHTML = words.map((w,i)=>
            `<span style="opacity:0;filter:blur(12px);display:inline-block;transform:translateY(-30px);animation:blurFadeIn 1.6s cubic-bezier(.4,1,.4,1) ${0.22 + i*0.13}s forwards;">${w}</span> `
          ).join("");
        }
      }, []);

      // 4. Update Placeholder
      useEffect(() => {
        setPlaceholder(typedPlaceholder || "Search colleges or courses...");
      }, [typedPlaceholder]);

      // 5. Scroll Observer (Animation Fix)
    // 5. Effect: Scroll Observer for Search Results & College Grid (Animation Fix)
      useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible'); // This triggers the CSS fade-in
              observer.unobserve(entry.target); 
            }
          });
        }, { threshold: 0.1 });

        // Wait for DOM update then attach observer to BOTH card types
        setTimeout(() => {
            const cards = document.querySelectorAll('.result-card, .college-card'); // <--- UPDATED SELECTOR
            cards.forEach((card) => observer.observe(card));
        }, 100);

        return () => observer.disconnect();
      }, [searchResults]); // Re-run when search results change (College grid is static, so this is fine)

      // --- CORE SEARCH LOGIC ---

      // FULL NORMALIZE QUERY FUNCTION
      const normalizeQuery = (q) => {
        const base = q.trim().toLowerCase();
        if (!base) return [];

        const keywords = [base];

        // B.Tech variations
        if (base === "btech" || base === "b.tech" || base === "b tech") {
          keywords.push(
            "b.tech", "btech", "b tech", "bachelor of technology",
            "btech cse", "b.tech cse", "btech computer science",
            "btech mechanical", "btech civil", "btech ece", "btech it"
          );
        }

        // BCA
        if (base === "bca" || base === "b.c.a") {
          keywords.push("bca", "b.c.a", "bachelor of computer applications", "computer applications");
        }

        // BBA
        if (base === "bba" || base === "b.b.a") {
          keywords.push("bba", "b.b.a", "bachelor of business administration", "business administration");
        }

        // Nursing
        if (base.includes("nursing") || base === "bsc nursing" || base === "gnm") {
          keywords.push(
            "bsc nursing", "b.sc nursing", "bsc nsg", "bachelor of science in nursing",
            "gnm", "general nursing and midwifery", "post basic bsc nursing",
            "post basic b.sc nursing", "p.b.bsc nursing"
          );
        }

        // Pharmacy
        if (base.includes("pharma") || base.includes("pharm")) {
          keywords.push(
            "bpharm", "b.pharm", "bpharma", "b.pharma", "b pharmacy",
            "bachelor of pharmacy", "dpharm", "d.pharm", "dpharma",
            "d.pharma", "diploma in pharmacy"
          );
        }

        // Physiotherapy
        if (base === "bpt" || base.includes("physio")) {
          keywords.push("bpt", "bachelor of physiotherapy", "physiotherapy", "mpt", "master of physiotherapy");
        }

        // Agriculture
        if (base.includes("agri")) {
          keywords.push("bsc agriculture", "b.sc agriculture", "bsc agri", "b.sc agri", "msc agriculture", "m.sc agriculture");
        }

        // Hotel Management
        if (base.includes("hotel") || base === "bhm" || base === "b.h.m") {
          keywords.push("bhm", "b.h.m", "bachelor of hotel management", "hotel management", "diploma in hotel management");
        }

        // Public / Hospital Health Admin
        if (base.includes("hospital administration") || base === "bha" || base === "mha") {
          keywords.push(
            "bha", "b.h.a", "bachelor in hospital administration", "hospital administration",
            "mha", "m.h.a", "master in hospital administration"
          );
        }

        if (base.includes("public health") || base === "bph" || base === "mph") {
          keywords.push(
            "bph", "b.p.h", "bachelor in public health", "public health",
            "mph", "m.p.h", "master in public health"
          );
        }

        // Generic science / commerce / arts
        if (base === "bsc" || base === "b.sc") keywords.push("bsc", "b.sc", "bachelor of science");
        if (base === "ba" || base === "b.a") keywords.push("ba", "b.a", "bachelor of arts");
        if (base === "bcom" || base === "b.com") keywords.push("bcom", "b.com", "bachelor of commerce");
        if (base === "mba") keywords.push("mba", "master of business administration");
        if (base === "mtech" || base === "m.tech") keywords.push("mtech", "m.tech", "master of technology", "mtech cse", "m.tech cse");

        return Array.from(new Set(keywords));
      };

      // HANDLE SEARCH
    // In HomePage.js - Replace handleSearch and related code:

  const HANDLE_SEARCH = useCallback((shouldScroll = false) => {
    const raw = searchQuery.trim().toLowerCase();
    if (!raw) {
      setSearchResults([]);
      return;
    }

    const keywords = normalizeQuery(raw);
    const results = [];

    collegesDetails.forEach((college) => {
      // 1. Check Courses
      if (Array.isArray(college.courses)) {
        college.courses.forEach((course) => {
          const textName = (course.name || '').toLowerCase();
          const textStream = (course.stream || '').toLowerCase();
          const textLevel = (course.level || '').toLowerCase();
          const match = keywords.some(kw => 
            textName.includes(kw) || textStream.includes(kw) || textLevel.includes(kw)
          );
          
          if (match) {
            results.push({
              type: 'course',
              collegeSlug: college.slug,
              collegeName: college.name,
              collegeLocation: college.location,
              collegeImage: college.image,
              courseName: course.name,
              level: course.level,
              stream: course.stream,
              duration: course.duration,
              fees: course.fees,
              eligibility: course.eligibility,
              avgPackage: course.avgPackage,
            });
          }
        });
      }

      // 2. Check College Names
      const name = (college.name || '').toLowerCase();
      const location = (college.location || '').toLowerCase();
      if (name.includes(raw) || location.includes(raw)) {
        results.push({
          type: 'college',
          collegeSlug: college.slug,
          collegeName: college.name,
          collegeLocation: college.location,
          collegeImage: college.image,
        });
      }
    });

    setSearchResults(results);

    // ✅ FIX: Only scroll AFTER state update completes
    if (shouldScroll && results.length > 0) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  }, [searchQuery, collegesDetails]);

  // 6. Trigger Search on Typing - Debounced, NO SCROLL
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (searchQuery.trim() !== '') {
        HANDLE_SEARCH(false); // false = do not scroll
      } else {
        setSearchResults([]);
      }
    }, 300);
    
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, HANDLE_SEARCH]);

  // 7. Manual Submit (Enter Key / Button Click) - FORCE SCROLL
  const onManualSubmit = (e) => {
    e.preventDefault();
    HANDLE_SEARCH(true); // true = force scroll
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };



      // Structured data for homepage
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "College Aggregator",
        "url": "https://www.collegeaggregator.com",
        "description": "Find and compare best colleges in Dehradun for Engineering, Management, Medical and other courses",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://www.collegeaggregator.com/search?q={search_term_string}",
          "query-input": "required name=search_term_string"
        },
        "areaServed": {
          "@type": "City",
          "name": "Dehradun",
          "addressCountry": "IN"
        }
      };

    const handleCollegeApply = (collegeName) => {
      setSelectedCollege({ name: collegeName, source: "homepage_card" });
      setApplyModalOpen(true);
    };
      
    useEffect(() => {
      // Add canonical tag
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = 'https://www.collegeaggregator.com/';
      document.head.appendChild(link);
      
      // Cleanup function
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }, []);

    return (
        <div className="App">
          <SEO 
            title="College Aggregator - Find Best Colleges in Dehradun | Engineering, Medical, Management"
            description="Discover top colleges in Dehradun including UPES, UIT, Graphic Era, HIT. Compare courses, fees, placements for B.Tech, BCA, MBA, Medical & more. Get expert admission guidance."
            keywords="colleges in dehradun, engineering colleges dehradun, UPES dehradun, UIT dehradun, Graphic Era University, best colleges uttarakhand, btech admission dehradun, MBA colleges dehradun, medical colleges dehradun"
            canonical="/"
            structuredData={structuredData}
          />
          <Navbar />

          {/* --- HERO SECTION --- */}
          <section className="hero" id="home" role="banner" aria-label="Hero section" style={{ position: "relative", zIndex: 1 }}>
            
            {/* 1. Mobile Social Icons (Top Right) */}
            <div className="hero-social-mobile">
              <a href="https://www.facebook.com/collegeaggregator?mibextid=ZbWKwL" target="_blank" rel="noreferrer">
                <img src="/fb.png" alt="Facebook" />
              </a>
              <a href="https://instagram.com/collegeaggregator?igshid=ZDdkNTZiNTM=" target="_blank" rel="noreferrer">
                <img src="/insta.png" alt="Instagram" />
              </a>
              <a href="https://www.linkedin.com/company/collegeaggregator/" target="_blank" rel="noreferrer">
                <img src="/linkedin.png" alt="LinkedIn" />
              </a>
            </div>

            {/* 2. Background Slideshows (Pointer Events NONE to prevent blocking) */}
            <div className="hero-slideshow hero-slideshow-top">
              <div className="hero-slide" style={{ backgroundImage: "url(/images/mlp3.webp)" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/mlp4.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/mlp1.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp.webp')" }}></div>

            </div>

            <div className="hero-slideshow hero-slideshow-bottom">
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp4.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp5.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/mlp2.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp3.webp')" }}></div>

            </div>

            <div className="hero-slideshow hero-slideshow-full">
              <div className="hero-slide" style={{ backgroundImage: "url(/images/mlp3.webp)" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp2.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp3.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp4.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp5.webp')" }}></div>
              <div className="hero-slide" style={{ backgroundImage: "url('/images/lp.webp')" }}></div>
            </div>

            {/* 3. Overlay & Decoration */}
            <div className="hero-overlay"></div>
            <div className="wave-animation">
              <svg viewBox="0 0 1200 100" preserveAspectRatio="none">
                <path className="wave" d="M0,50 Q300,0 600,50 T1200,50 L1200,100 L0,100 Z" fill="rgba(37, 99, 235, 0.1)" />
                <path className="wave" d="M0,60 Q300,10 600,60 T1200,60 L1200,100 L0,100 Z" fill="rgba(37, 99, 235, 0.05)" />
              </svg>
            </div>

            {/* 4. Hero Content & Search Bar */}
            <div className="hero-content" style={{ position: "relative", zIndex: 10 }}>
              <h1 className="hero-animated-title"> Find Your Perfect College in Dehradun</h1>
              <p className="hero-subtitle"> Discover excellence in education with comprehensive guidance and support</p>
              
            <div className="hero-search-bar" role="search" aria-label="College search" style={{ position: "relative", zIndex: 20 }}>
                <div className="search-wrapper" style={{ position: "relative", zIndex: 21 }}>
                  
                  <form 
                    onSubmit={onManualSubmit}
                    style={{ position: 'relative', zIndex: 25, width: '100%', display: 'flex', alignItems: 'center' }}
                  >
                    <input
                      ref={searchInputRef}
                      type="text"
                      className="search-input"
                      placeholder={placeholder}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      // Input gets Z-Index 30
                      style={{ 
                        width: '100%',
                        position: 'relative', 
                        zIndex: 30, 
                        pointerEvents: 'auto',
                        paddingRight: '50px' /* Crucial: Make room for the button so text doesn't hide behind it */
                      }}
                    />

                    {/* NUCLEAR FIX: Button Wrapper Z-Index 100 */}
                    <div 
                      style={{ 
                        position: 'absolute', 
                        right: '5px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        zIndex: 100, /* Highest Priority */
                        pointerEvents: 'auto', /* Force clickable */
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <button 
                        type="submit" 
                        onClick={onManualSubmit}
                        aria-label="Search"
                        style={{ 
                          background: 'transparent', 
                          border: 'none', 
                          cursor: 'pointer', 
                          padding: '10px', 
                          margin: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '44px', 
                          minHeight: '44px'
                        }}
                      >
                        {/* SVG ignores clicks so they hit the button */}
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="22" height="22" viewBox="0 0 20 20"
                          style={{ pointerEvents: 'none', display: 'block' }} 
                        >
                          <path fill="#000000" d="M8.195 0c4.527 0 8.196 3.62 8.196 8.084a7.989 7.989 0 0 1-1.977 5.267l5.388 5.473a.686.686 0 0 1-.015.98a.71.71 0 0 1-.993-.014l-5.383-5.47a8.23 8.23 0 0 1-5.216 1.849C3.67 16.169 0 12.549 0 8.084C0 3.62 3.67 0 8.195 0Zm0 1.386c-3.75 0-6.79 2.999-6.79 6.698c0 3.7 3.04 6.699 6.79 6.699s6.791-3 6.791-6.699c0-3.7-3.04-6.698-6.79-6.698Z"/>
                        </svg>
                      </button>
                    </div>
                  </form>

                  {/* Typing Placeholder */}
                  <div style={{ pointerEvents: "none", position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 26 }}>
                    <TextType
                      text={[
                        "Search colleges or courses...",
                        "Search UPES, UIT, HIT...",
                        'Try "B.Tech CSE" or "MBA"',
                      ]}
                      typingSpeed={100}
                      pauseDuration={1500}
                      showCursor={true}
                      className="sr-only"
                      loop={true}
                      startOnVisible={false}
                    />
                  </div>

                </div>
              </div>
            </div>
          </section>

          {/* --- SEARCH RESULTS SECTION --- */}
          {searchResults.length > 0 && (
            <section className="search-results" ref={resultsRef} style={{ position: "relative", zIndex: 10 }}>
              <div className="results-header">
                <h2>Search Results for <span>"{searchQuery}"</span></h2>
                <p>{searchResults.length} match{searchResults.length !== 1 ? 'es' : ''} found</p>
              </div>
              
              <div className="results-list">
                {searchResults.map((item, index) => (
                  <div 
                    key={index} 
                    className="result-card" 
                    onClick={() => navigate(`/college/${item.collegeSlug}`)}
                  >
                    {/* Thumbnail Image */}
                    <div className="result-img-wrapper">
                      <img src={item.collegeImage || "/images/placeholder.jpg"} alt={item.collegeName} loading="lazy" />
                      <span className={`result-badge badge-${item.type}`}>
                        {item.type === 'college' ? 'Institute' : 'Course'}
                      </span>
                    </div>

                    {/* Content Area */}
                    <div className="result-content">
                      {item.type === "course" ? (
                        <>
                          <h3 className="result-title">{item.courseName}</h3>
                          <p className="result-subtitle">at {item.collegeName}</p>
                          
                          <div className="result-meta-grid">
                            <div className="meta-item">
                              <span className="label">Duration:</span> {item.duration}
                            </div>
                          
                          </div>
                        </>
                      ) : (
                        <>
                          <h3 className="result-title">{item.collegeName}</h3>
                          <p className="result-location">📍 {item.collegeLocation}</p>
                          <p className="result-desc">Top Rated Institute in Dehradun</p>
                        </>
                      )}

                      {/* Action Arrow */}
                      <div className="result-action">
                        <span>View Details</span>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* --- STATS SECTION --- */}
        <section className="stats">
    <div className="stats-container">
      
      {/* Colleges: 3 seconds */}
      <div className="stat-card">
        <div className="stat-number">
          <CountUp from={0} to={20} duration={3} className="count-up-text" start={true} />+
        </div>
        <div className="stat-label">Colleges</div>
      </div>

      {/* Courses: 3 seconds */}
      <div className="stat-card">
        <div className="stat-number">
          <CountUp from={0} to={60} duration={3} className="count-up-text" start={true} />+
        </div>
        <div className="stat-label">Courses</div>
      </div>

      {/* Support: 3 seconds */}
      <div className="stat-card">
        <div className="stat-number">
          <CountUp from={0} to={100} duration={3} className="count-up-text" start={true} />%
        </div>
        <div className="stat-label">Support</div>
      </div>

      {/* Students: 3 seconds */}
      <div className="stat-card">
        <div className="stat-number">
          <CountUp from={0} to={900} duration={3} separator="," className="count-up-text" start={true} />+
        </div>
        <div className="stat-label">Students</div>
      </div>

    </div>
  </section>

          {/* --- COLLEGE GRID --- */}
          <div className="colleges-grid">
            {collegesDetails.map((college, index) => (
              <div
                key={college.slug}
                ref={(el) => (cardRefs.current[index] = el)}
                className="college-card-link college-card"
                onClick={() => navigate(`/college/${college.slug}`)}
              >
                <div className="college-image">
                <img src={college.image} alt={`${college.name} campus in Dehradun - ${college.type}`} loading="lazy" />
                </div>

                <div className="college-content">
                  <div className="college-header">
                    <h3 className="college-name">{college.name}</h3>
                    <p className="college-location">📍 {college.location}</p> 
                  </div>

                  <div className="college-actions">
                    <div className="button-row-first">
    {/* ✅ SPECIAL HANDLING FOR UIT COLLEGE */}
    {college.slug === 'uit-college' ? (
      <button
        onClick={(e) => {
          e.stopPropagation();
          window.open('/applyuit', '_blank', 'noopener,noreferrer');
        }}
        className="apply-btn"
      >
  REGISTER NOW    </button>
    ) : (
      <button
        onClick={(e) => {
          e.stopPropagation();
          window.open(
            `/apply?college=${encodeURIComponent(college.name)}`, 
            '_blank', 
            'noopener,noreferrer'
          );
        }}
        className="apply-btn"
      >
        REGISTER NOW
      </button>
    )}
    
    <button
      className="btn-secondary"
      onClick={(e) => {
        e.stopPropagation();
        navigate(`/college/${college.slug}`);
      }}
    >
      Fee Structure
    </button>
  </div>
  <div className="button-row-second">
    <button
      className="btn-brochure"
      onClick={(e) => {
        e.stopPropagation();
        // Check if this is Graphic Era
        if (college.slug === 'graphic-era-dehradun') {
          setSelectedGeuCollege(college);
          setGeuModalOpen(true);
        } else {
          // For other colleges, open brochure directly
          window.open(college.brochure, '_blank', 'noopener,noreferrer');
        }
      }}
    >
      VIEW BROCHURE
    </button>
  </div>


                  </div>
                </div>
              </div>
            ))}
            <GeuModal 
    isOpen={geuModalOpen}
    onClose={() => {
      setGeuModalOpen(false);
      setSelectedGeuCollege(null);
    }}
    college={selectedGeuCollege}
  />

          </div>





          {/* --- ABOUT SECTION --- */}
          <section className="about" id="about">
            <div className="about-content">
              <h2 className="section-title">About CollegeAggregator</h2>
              <p>
                CollegeAggregator is a Dehradun-focused college discovery platform that helps local
                students find and compare colleges, courses, fees, and placements with clear,
                student-first guidance.
              </p>

              <div className="about-btn-row">
                <Link to="/about" className="about-shine-btn">
                  <span>About Us</span>
                </Link>
              </div>
            </div>
          </section>

          {/* --- WHY CHOOSE US --- */}
          <section className="why-choose">
            <h2 className="section-title">Why Choose Us ?</h2>
            <p className="section-subtitle">Comprehensive support for your educational journey.</p>
            <div className="why-grid">
              <div className="benefit-card">
                <div className="benefit-icon">👤</div>
                <h3 className="benefit-title">One-on-One Counseling</h3>
                <p className="benefit-description">Personalized guidance for your educational journey</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">⭐</div>
                <h3 className="benefit-title">Legitimate Reviews</h3>
                <p className="benefit-description">Honest college reviews and comparisons</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">💰</div>
                <h3 className="benefit-title">Fee Information</h3>
                <p className="benefit-description">Transparent college fee structures</p>
              </div>
              <div className="benefit-card">
                <div className="benefit-icon">🕐</div>
                <h3 className="benefit-title">24×7 Support</h3>
                <p className="benefit-description">Always available to assist you</p>
              </div>
            </div>
          </section>

          {/* --- TESTIMONIALS --- */}
          <TestimonialsSection />

          {/* --- FOOTER --- */}
          <footer className="site-footer">
            <div className="footer-inner">
              <div className="footer-brand">
                <h3>CollegeAggregator</h3>
                <p>Helping students discover the right colleges with transparent data on courses, fees, and placements.</p>
              </div>

              <div className="footer-columns">
                <div className="footer-col">
                  <h4>Support</h4>
                  <a href="#counselling">Free Counselling</a>
                  <a href="#faqs">FAQs</a>
                  <a href="#contact">Contact Us</a>
                </div>

                <div className="footer-col">
                  <h4>Contact</h4>
                  <p>Email: collegeaggregator@gmail.com</p>
                  <p>Mon–Sat, 10am–7pm IST</p>
                  <div className="footer-social">
                    <a href="https://www.facebook.com/collegeaggregator?mibextid=ZbWKwL" target="_blank" rel="noreferrer">FB</a>
                    <a href="https://instagram.com/collegeaggregator?igshid=ZDdkNTZiNTM=" target="_blank" rel="noreferrer">IG</a>
                    <a href="https://www.linkedin.com/company/collegeaggregator/" target="_blank" rel="noreferrer">in</a>
                    <a href="https://g.co/kgs/7wKFni" target="_blank" rel="noreferrer">G</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="footer-bottom">
              <span>© {new Date().getFullYear()} CollegeAggregator. All rights reserved.</span>
              <div className="footer-links">
                <a href="#terms">Terms</a>
                <a href="#privacy">Privacy</a>
              </div>
            </div>
          </footer>

          {/* --- APPLICATION MODAL --- */}
          <div className="apply-overlay" id="applyOverlay">
            <div className="apply-modal">
              <div className="apply-modal-header">
                <button 
                  onClick={() => window.open('/apply', '_blank', 'noopener,noreferrer')}
                  className="apply-btn"
                >
                  APPLY NOW
                </button>
              </div>

              <div className="apply-form-scroll">
                <form
                  id="applicationForm"
                  ref={formRef}
                  className="apply-form"
                  noValidate
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (phone.length !== 10) {
                      setPhoneError("Please enter a valid 10-digit mobile number.");
                      const phoneInput = document.getElementById("phone");
                      if (phoneInput) {
                        phoneInput.focus();
                        phoneInput.scrollIntoView({ behavior: "smooth", block: "center" });
                      }
                      return;
                    }
                    const form = formRef.current;
                    if (!form.checkValidity()) {
                      const firstInvalid = form.querySelector(":invalid");
                      if (firstInvalid) {
                        firstInvalid.focus();
                        firstInvalid.scrollIntoView({ behavior: "smooth", block: "center" });
                      }
                      return;
                    }
                    emailjs
                      .sendForm(
                        "service_u0g0bqj",
                        "template_naso7ph",
                        formRef.current,
                        "0JNndpSn5KZm_5yLJ"
                      )
                      .then(
                        () => {
                          formRef.current.style.display = "none";
                          const success = document.getElementById("successMessage");
                          if (success) success.classList.add("active");
                          setTimeout(closeModal, 3000);
                        },
                        (error) => {
                          console.error("EmailJS error", error);
                        }
                      );
                  }}
                >
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name</label>
                    <input id="fullName" name="name" type="text" required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email">Email</label>
                    <input id="email" name="email" type="email" required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">Mobile Number</label>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={phone}
                      required
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                        setPhone(digits);
                        setPhoneError("");
                      }}
                    />
                    {phoneError && (
                      <p className="error-text" style={{ color: "#d00", marginTop: "4px" }}>
                        {phoneError}
                      </p>
                    )}
                  </div>

                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input id="city" name="city" type="text" required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="course">Interested Course</label>
                    <select id="course" name="course" required>
                      <option value="">Select a course</option>
                      <option value="btech-cse">B.Tech CSE</option>
                      <option value="bba">BBA</option>
                      <option value="bca">BCA</option>
                      <option value="mba">MBA</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="qualification">Current Qualification</label>
                    <input id="qualification" name="qualification" type="text" required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="marks">Approx. Percentage / CGPA</label>
                    <input id="marks" name="marks" type="text" required />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message / Query (optional)</label>
                    <textarea
                      id="message"
                      name="message"
                      rows="3"
                      placeholder="Any questions or preferences?"
                    />
                  </div>

                  <button type="submit" className="submit-button">
                    Submit Application
                  </button>
                </form>

                <div className="success-message" id="successMessage">
                  <div className="success-icon">✓</div>
                  <h3 className="success-title">Application Submitted!</h3>
                  <p className="success-text">Thank you for your application. Our team will contact you shortly.</p>
                </div>
              </div>
            </div>
          </div>
          
        <ApplyModal
          isOpen={applyModalOpen}
          onClose={() => setApplyModalOpen(false)}
          collegeName={selectedCollege.name}
          source={selectedCollege.source}
        />

          <CommonApplicationForm 
            isOpen={showCommonForm} 
            onClose={() => setShowCommonForm(false)} 
          />
        </div>
        
      );
    }